/**
 * NivGrowTool — NiV → כלים → "Grow": callback inbox + unmatched payments browser
 * (grow_integration.md Phase 8.2). Replay an inbox doc, attach an unmatched
 * callback to a link, mark resolved. Since 2026-11-03 also the merchant-onboarding
 * inbox (`kind: "onboarding"`, grow_part2.md Phase 3d) with "שיוך לקליניקה". All via `nivGrowAdmin`. CK-9989.
 */

import { useState } from "react";
import { ArrowsClockwise, CreditCard, Eye, LinkSimple, PlayCircle, Check } from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import LoaderLoop from "../../../../components/shared/loader/LoaderLoop";
import { useToast } from "../../../../contexts/ToastContext";
import {
  useGetNivGrowInboxQuery,
  useGetNivGrowUnmatchedQuery,
  useRunNivGrowActionMutation,
} from "../../../../store/services/nivApi";

const formatWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("he-IL", { timeZone: "Asia/Jerusalem", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const OUTCOME_TONE = { applied: "ok", noop: "ok", recorded: "ok", unmatched: "warn", error: "bad", parked: "warn", partial: "warn" };

export default function NivGrowTool({ isAuthorized }) {
  const { showSuccess, showError } = useToast();
  const [kind, setKind] = useState("notify"); // notify | invoice
  const [outcome, setOutcome] = useState("");
  const [clinicFilter, setClinicFilter] = useState("");
  const [openDoc, setOpenDoc] = useState(null);
  const [attachFor, setAttachFor] = useState(null); // unmatched row
  const [attachForm, setAttachForm] = useState({ clinicId: "", linkId: "" });
  const [onboardFor, setOnboardFor] = useState(null); // onboarding inbox row
  const [onboardForm, setOnboardForm] = useState({ clinicId: "", pageCode: "", accountPageCode: "" });

  const { data: inbox = [], isLoading: inboxLoading, refetch: refetchInbox } = useGetNivGrowInboxQuery(
    { kind, clinicId: clinicFilter || undefined, outcome: outcome || undefined, limit: 80 },
    { skip: !isAuthorized }
  );
  const { data: unmatched = [], isLoading: umLoading, refetch: refetchUm } = useGetNivGrowUnmatchedQuery(
    { clinicId: clinicFilter || undefined, resolved: false, limit: 80 },
    { skip: !isAuthorized }
  );
  const [run, { isLoading: running }] = useRunNivGrowActionMutation();

  const act = async (action, payload, okMsg) => {
    const res = await run({ action, payload });
    if (res.error) {
      console.error("[CK-9989] NiV Grow tool action failed", { errorCode: "CK-9989", action, errorMessage: res.error.message, timestamp: new Date().toISOString() });
      showError(res.error.message || "הפעולה נכשלה");
      return null;
    }
    showSuccess(okMsg);
    return res.data;
  };

  const replay = async (row) => {
    const data = await act("replay", { inboxId: row.id, kind }, "הופעל מחדש");
    if (data?.result) setOpenDoc({ ...row, result: data.result });
  };

  const onboardSubmit = async () => {
    if (!onboardFor || !onboardForm.clinicId) return;
    const data = await act("attachOnboarding", { inboxId: onboardFor.id, clinicId: onboardForm.clinicId.trim(), pageCode: onboardForm.pageCode.trim() || undefined, accountPageCode: onboardForm.accountPageCode.trim() || undefined }, "החיווי שויך והקליניקה חוברה ל-Grow");
    if (data) {
      setOnboardFor(null);
      setOnboardForm({ clinicId: "", pageCode: "", accountPageCode: "" });
      refetchInbox();
    }
  };

  const attachSubmit = async () => {
    if (!attachFor || !attachForm.clinicId || !attachForm.linkId) return;
    const data = await act("attachUnmatched", { unmatchedId: attachFor.id, clinicId: attachForm.clinicId.trim(), linkId: attachForm.linkId.trim() }, "העדכון שויך לקישור ועובד מחדש");
    if (data) {
      setAttachFor(null);
      setAttachForm({ clinicId: "", linkId: "" });
      refetchUm();
      refetchInbox();
    }
  };

  if (!isAuthorized) return null;

  return (
    <div className="niv-push-content ngw">
      <div className="niv-push-header">
        <h3><CreditCard size={20} weight="fill" /> Grow — עדכוני תשלום</h3>
        <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" onClick={() => { refetchInbox(); refetchUm(); }} disabled={inboxLoading || umLoading}>
          <ArrowsClockwise size={14} className={inboxLoading || umLoading ? "spinning" : ""} /> רענון
        </button>
      </div>

      {/* Unmatched first — these need a human */}
      <section className="nwa-section">
        <div className="nwa-section-head">
          <h4>לא שויכו ({unmatched.length})</h4>
          <input className="niv-push-input nwa-log-filter" placeholder="סינון לפי clinicId" value={clinicFilter} onChange={(e) => setClinicFilter(e.target.value.trim())} dir="ltr" />
        </div>
        {umLoading ? (
          <ContentLoader size="sm" compact />
        ) : unmatched.length === 0 ? (
          <p className="nwa-note">אין עדכונים שממתינים לשיוך.</p>
        ) : (
          <div className="niv-info-table-scroll">
            <table className="nwa-table">
              <thead><tr><th>זמן</th><th>סוג</th><th>סיבה</th><th>קליניקה</th><th>קישור</th><th>סכום</th><th>פעולות</th></tr></thead>
              <tbody>
                {unmatched.map((u) => (
                  <tr key={u.id}>
                    <td>{formatWhen(u.createdAt)}</td>
                    <td>{u.kind}</td>
                    <td><span className="nwa-pill nwa-pill-warn">{u.reason}</span></td>
                    <td dir="ltr">{u.clinicId || "—"}</td>
                    <td dir="ltr">{u.linkId || "—"}</td>
                    <td>{u.summary?.sum ?? "—"}</td>
                    <td style={{ display: "flex", gap: 4 }}>
                      {u.inboxId && <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" disabled={running} onClick={() => { setAttachFor(u); setAttachForm({ clinicId: u.clinicId || "", linkId: u.linkId || "" }); }}><LinkSimple size={13} /> שיוך לקישור</button>}
                      <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" disabled={running} onClick={async () => { if (await act("resolveUnmatched", { unmatchedId: u.id, note: "resolved from NiV" }, "סומן כטופל")) refetchUm(); }}><Check size={13} /> טופל</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {attachFor && (
          <div className="niv-push-form-group" style={{ marginTop: 8 }}>
            <label className="niv-push-label">שיוך עדכון {attachFor.id} לקישור</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <input className="niv-push-input" dir="ltr" placeholder="clinicId" value={attachForm.clinicId} onChange={(e) => setAttachForm({ ...attachForm, clinicId: e.target.value })} />
              <input className="niv-push-input" dir="ltr" placeholder="linkId" value={attachForm.linkId} onChange={(e) => setAttachForm({ ...attachForm, linkId: e.target.value })} />
              <button type="button" className="niv-btn niv-btn-primary niv-btn-sm" disabled={running || !attachForm.clinicId || !attachForm.linkId} onClick={attachSubmit}>{running ? <LoaderLoop size="xs" /> : <PlayCircle size={13} />} עיבוד מחדש</button>
              <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" onClick={() => setAttachFor(null)}>ביטול</button>
            </div>
            <p className="nwa-note">מזריק cField1/cField2 + מזהה הקישור לפיילוד המקורי ומריץ את העיבוד הרגיל (כולל approve).</p>
          </div>
        )}
      </section>

      {/* Inbox */}
      <section className="nwa-section">
        <div className="nwa-section-head">
          <h4>Inbox ({inbox.length})</h4>
          <div style={{ display: "flex", gap: 6 }}>
            <select className="niv-push-input nwa-log-filter" value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="notify">notify (תשלומים)</option>
              <option value="invoice">invoice (קבלות)</option>
              <option value="onboarding">onboarding (הקמות בתי עסק)</option>
            </select>
            <select className="niv-push-input nwa-log-filter" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
              <option value="">כל התוצאות</option>
              <option value="applied">applied</option>
              <option value="noop">noop</option>
              <option value="recorded">recorded</option>
              <option value="unmatched">unmatched</option>
              <option value="error">error</option>
            </select>
          </div>
        </div>
        {inboxLoading ? (
          <ContentLoader size="sm" compact />
        ) : inbox.length === 0 ? (
          <p className="nwa-note">אין עדכונים.</p>
        ) : kind === "onboarding" ? (
          <div className="niv-info-table-scroll">
            <table className="nwa-table">
              <thead><tr><th>התקבל</th><th>סביבה</th><th>IP</th><th>תוצאה</th><th>ref / קליניקה</th><th>עסק</th><th>פעולות</th></tr></thead>
              <tbody>
                {inbox.map((r) => {
                  const m = r.match || {};
                  const tone = m.outcome === "attached" ? "ok" : m.outcome ? "warn" : "bad";
                  return (
                    <tr key={r.id}>
                      <td>{formatWhen(r.receivedAt)}</td>
                      <td>{r.env || "—"}</td>
                      <td dir="ltr">{r.ip || "—"}{r.ipAllowed === false ? " ⚠" : ""}</td>
                      <td><span className={`nwa-pill nwa-pill-${tone}`}>{m.outcome || "pending"}{m.reason ? ` · ${m.reason}` : ""}</span></td>
                      <td dir="ltr">{m.clinicId || r.query?.ref || r.fields?.ref || r.fields?.externalId || "—"}</td>
                      <td>{r.fields?.businessName || r.fields?.name || "—"}</td>
                      <td style={{ display: "flex", gap: 4 }}>
                        <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" onClick={() => setOpenDoc(r)}><Eye size={13} /> פרטים</button>
                        {m.outcome !== "attached" && (
                          <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" disabled={running} onClick={() => { setOnboardFor(r); setOnboardForm({ clinicId: m.clinicId || r.query?.ref || "", pageCode: "", accountPageCode: "" }); }}><LinkSimple size={13} /> שיוך לקליניקה</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {onboardFor && (
              <div className="niv-push-form-group" style={{ marginTop: 8 }}>
                <label className="niv-push-label">שיוך חיווי הקמה {onboardFor.id} לקליניקה</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <input className="niv-push-input" dir="ltr" placeholder="clinicId" value={onboardForm.clinicId} onChange={(e) => setOnboardForm({ ...onboardForm, clinicId: e.target.value })} />
                  <input className="niv-push-input" dir="ltr" placeholder="pageCode (ריק = של הפלטפורמה)" value={onboardForm.pageCode} onChange={(e) => setOnboardForm({ ...onboardForm, pageCode: e.target.value })} />
                  <input className="niv-push-input" dir="ltr" placeholder="account pageCode (אופציונלי)" value={onboardForm.accountPageCode} onChange={(e) => setOnboardForm({ ...onboardForm, accountPageCode: e.target.value })} />
                  <button type="button" className="niv-btn niv-btn-primary niv-btn-sm" disabled={running || !onboardForm.clinicId} onClick={onboardSubmit}>{running ? <LoaderLoop size="xs" /> : <PlayCircle size={13} />} חיבור</button>
                  <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" onClick={() => setOnboardFor(null)}>ביטול</button>
                </div>
                <p className="nwa-note">קורא את ה-userId מהחיווי (לא ממוסך בשרת), כותב secure/grow ומסמן את הקליניקה כמחוברת. ללא pageCode משתמש ב-GROW_PLATFORM_PAGE_CODE מה-env.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="niv-info-table-scroll">
            <table className="nwa-table">
              <thead><tr><th>התקבל</th><th>סביבה</th><th>IP</th><th>תוצאה</th><th>קליניקה / קישור</th><th>סטטוס Grow</th><th>פעולות</th></tr></thead>
              <tbody>
                {inbox.map((r) => {
                  const tone = OUTCOME_TONE[r.result?.outcome] || (r.processed ? "warn" : "bad");
                  return (
                    <tr key={r.id}>
                      <td>{formatWhen(r.receivedAt)}</td>
                      <td>{r.env || "—"}</td>
                      <td dir="ltr">{r.ip || "—"}{r.ipAllowed === false ? " ⚠" : ""}</td>
                      <td><span className={`nwa-pill nwa-pill-${tone}`}>{r.result?.outcome || (r.processed ? "processed" : "pending")}{r.result?.reason ? ` · ${r.result.reason}` : ""}</span></td>
                      <td dir="ltr">{r.result?.clinicId || r.fields?.customFields?.cField2 || "—"}<br />{r.result?.linkId || r.fields?.customFields?.cField1 || ""}</td>
                      <td>{r.fields?.statusCode ?? "—"} {r.fields?.status || ""}</td>
                      <td style={{ display: "flex", gap: 4 }}>
                        <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" onClick={() => setOpenDoc(r)}><Eye size={13} /> פרטים</button>
                        <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" disabled={running} onClick={() => replay(r)}><PlayCircle size={13} /> replay{r.replays ? ` (${r.replays})` : ""}</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {openDoc && (
        <section className="nwa-section">
          <div className="nwa-section-head">
            <h4>פיילוד {openDoc.id} (PII ממוסך)</h4>
            <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" onClick={() => setOpenDoc(null)}>סגירה</button>
          </div>
          <pre className="niv-push-result" dir="ltr" style={{ maxHeight: 360, overflow: "auto", whiteSpace: "pre-wrap" }}>
            {JSON.stringify({ result: openDoc.result, match: openDoc.match, query: openDoc.query, fields: openDoc.fields, ip: openDoc.ip, ipAllowed: openDoc.ipAllowed, contentType: openDoc.contentType }, null, 2)}
          </pre>
        </section>
      )}
    </div>
  );
}
